'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedSvgSource } from '@/components/tools/svg-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  createTextDownloadBlob,
  detectSvgColors,
  formatFileSize,
  getBaseName,
  replaceSvgColors,
  type SvgColorEntry,
} from '@/lib/browser-svg-tools';

export default function BrowserSvgColorReplacer() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadFile, loadMarkup, clearSource } = useLoadedSvgSource();
  const [pastedMarkup, setPastedMarkup] = useState('');
  const [colors, setColors] = useState<SvgColorEntry[]>([]);
  const [replacements, setReplacements] = useState<Record<string, string>>({});
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultName, setResultName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (resultUrl) {
        URL.revokeObjectURL(resultUrl);
      }
    };
  }, [resultUrl]);

  useEffect(() => {
    if (!source) {
      setColors([]);
      setReplacements({});
      return;
    }

    try {
      const detected = detectSvgColors(source.originalMarkup);
      setColors(detected);
      setReplacements(
        detected.reduce<Record<string, string>>((accumulator, entry) => {
          accumulator[entry.normalized] = entry.normalized;
          return accumulator;
        }, {}),
      );
    } catch {
      setColors([]);
      setReplacements({});
    }
  }, [source]);

  function resetOutputState() {
    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }

    setResultUrl(null);
    setResultName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadFile(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The SVG could not be loaded.');
    }
  }

  function handleMarkupLoad() {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      loadMarkup(pastedMarkup);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The pasted SVG could not be loaded.',
      );
    }
  }

  async function runReplacement() {
    if (!source) {
      setErrorMessage('Choose one SVG or paste valid SVG markup first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'svg-color-replacer',
        'Credits are not available for another SVG color replacement right now.',
      );
      const resultMarkup = replaceSvgColors(source.originalMarkup, replacements);
      const blob = createTextDownloadBlob(resultMarkup, 'image/svg+xml;charset=utf-8');

      resetOutputState();
      setResultUrl(URL.createObjectURL(blob));
      setResultName(`${getBaseName(source.name)}-colors.svg`);
      setResultSize(blob.size);
      setCreditMessage(formatToolCreditSuccess('SVG color replacement', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The SVG colors could not be updated.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Replace SVG fill and stroke colors</h2>
          <p>
            Detect the colors used inside one SVG, swap them globally or one by one, and export the
            updated markup locally in the browser.
          </p>
        </div>
        <span className="tool-live-badge">Colors</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.name : 'Choose one SVG or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.originalSize)} • ${colors.length} detected colors`
                  : 'Upload one .svg file or paste SVG markup below'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/svg+xml,.svg"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <label className="tool-live-field tool-live-field-full">
            <span>SVG markup</span>
            <textarea
              onChange={(event) => setPastedMarkup(event.target.value)}
              placeholder="<svg viewBox='0 0 24 24'>…</svg>"
              rows={7}
              value={pastedMarkup}
            />
          </label>

          <div className="hero-actions tool-live-actions">
            <button
              className="button button-ghost"
              disabled={!pastedMarkup.trim()}
              onClick={handleMarkupLoad}
              type="button"
            >
              Use pasted SVG
            </button>
          </div>

          {colors.length ? (
            <div className="tool-png-palette-grid">
              {colors.slice(0, 24).map((entry) => (
                <article className="tool-png-palette-card" key={entry.normalized}>
                  <div className="tool-png-palette-chip" style={{ background: entry.normalized }} />
                  <strong>{entry.normalized}</strong>
                  <span>{entry.count} uses</span>
                  <input
                    onChange={(event) => {
                      setReplacements((current) => ({
                        ...current,
                        [entry.normalized]: event.target.value,
                      }));
                      resetOutputState();
                    }}
                    type="color"
                    value={replacements[entry.normalized] ?? entry.normalized}
                  />
                </article>
              ))}
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || !colors.length || isProcessing}
              onClick={() => void runReplacement()}
              type="button"
            >
              {isProcessing ? 'Applying…' : 'Apply and export'}
            </button>
            {resultUrl && resultName ? (
              <a className="button button-ghost" download={resultName} href={resultUrl}>
                Download SVG
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Color summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Detected colors</strong>
              <span>{colors.length || 'Load one SVG first'}</span>
            </div>
            <div>
              <strong>Source</strong>
              <span>{source ? formatFileSize(source.originalSize) : 'No SVG loaded'}</span>
            </div>
            <div>
              <strong>Result</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            <div>
              <strong>Privacy</strong>
              <span>Processed in your browser</span>
            </div>
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Source SVG</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Source SVG preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Source preview</strong>
                  <span>Load one SVG to inspect the current colors.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>Updated SVG</strong>
            <div className="tool-live-preview-frame">
              {resultUrl ? (
                <img alt="Updated SVG preview" src={resultUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Updated preview</strong>
                  <span>Apply the color replacements to preview the updated SVG.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
